import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Phone, MapPin, Send, GraduationCap, Users, 
  Building, Clock, Globe, ArrowRight, CheckCircle, 
  AlertTriangle, RefreshCw, Navigation, ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import FloatingLabelInput from '../components/FloatingLabelInput';
import api from '../utils/axios';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error message when user starts typing
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    
    try {
      await api.post('/contacts', formData);
      setStatus('success');
      // Reset form after success
      setTimeout(() => {
        setStatus('idle');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }, 3000);
    } catch (err) {
      // Handle different types of errors
      if (err.response) {
        const errorData = err.response.data;
        if (errorData.message) {
          setErrorMessage(errorData.message);
        } else if (err.response.status === 400) {
          setErrorMessage('Please check your form data and try again.');
        } else if (err.response.status === 500) {
          setErrorMessage('Server error. Please try again later.');
        } else {
          setErrorMessage('Something went wrong. Please try again.');
        }
      } else if (err.request) {
        setErrorMessage('Network error. Please check your internet connection and try again.');
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
      
      setStatus('error');
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Simple Map Point Component
  const MapPointComponent = ({ location, title, address, coordinates, color, phone, email, timing }) => {
    return (
      <div className="h-80 relative bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-700 dark:to-gray-800 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-600">
        {/* Map-like background with grid pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="w-full h-full" style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.2) 1px, transparent 1px)
            `,
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        
        {/* Location marker with pulse animation */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            <div className={`w-12 h-12 ${color} rounded-full flex items-center justify-center shadow-2xl animate-pulse`}>
              <MapPin className="w-7 h-7 text-white" />
            </div>
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2">
              <div className="w-0 h-0 border-l-6 border-r-6 border-t-6 border-transparent border-t-blue-500"></div>
            </div>
          </div>
        </div>
        
        {/* Coordinates display */}
        <div className="absolute top-4 left-4 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 shadow-lg">
          <div className="text-xs text-gray-600 dark:text-gray-400">Coordinates</div>
          <div className="text-sm font-mono text-gray-800 dark:text-gray-200">
            {coordinates[0].toFixed(4)}, {coordinates[1].toFixed(4)}
          </div>
        </div>
        
        {/* Location info overlay */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-xl border border-gray-100 dark:border-gray-700">
            <h4 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center">
              <MapPin className={`w-5 h-5 ${color.replace('bg-', 'text-')} mr-2`} />
              {title}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">{address}</p>
            
            {/* Contact details */}
            <div className="space-y-2 mb-4 text-sm">
              {phone && (
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <Phone className="w-4 h-4 mr-2 text-blue-500" />
                  <a href={`tel:${phone}`} className="hover:underline">{phone}</a>
                </div>
              )}
              {email && (
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <Mail className="w-4 h-4 mr-2 text-blue-500" />
                  <a href={`mailto:${email}`} className="hover:underline">{email}</a>
                </div>
              )}
              {timing && (
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <Clock className="w-4 h-4 mr-2 text-blue-500" />
                  <span>{timing}</span>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2">
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Navigation className="w-4 h-4 mr-1" />
                Get Directions
              </a>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <Globe className="w-4 h-4 mr-1" />
                View on Map
              </a>
            </div>
          </div>
        </div>
        
        {/* Floating action button */}
        <div className="absolute top-4 right-4">
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white dark:bg-gray-800 p-3 rounded-full shadow-lg hover:shadow-xl transition-shadow"
            title="Open in Google Maps"
          >
            <ExternalLink className="w-5 h-5 text-blue-600" />
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 pt-20 overflow-hidden">
      {/* Enhanced Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-professional opacity-95"></div>
        <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
        
        <div className="container-max relative z-10 py-20">
          <div className="text-center text-white">
            <motion.div
              className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8"
              {...fadeInUp}
            >
              <Mail className="w-5 h-5 mr-2 text-yellow-400" />
              <span className="text-sm font-medium">Get In Touch</span>
            </motion.div>

            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Ready to Start Your{' '}
              <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                Programming Journey
              </span>
              ?
            </motion.h1>

            <motion.p 
              className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed text-gray-100"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Contact us today and take the first step towards your dream career! 
              Our expert team is here to guide you through every step of your learning journey.
            </motion.p>

            {/* Quick Stats */}
            <motion.div 
              className="flex flex-col md:flex-row justify-center items-center gap-8 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                <div className="text-4xl font-bold text-yellow-400 mb-2">24/7</div>
                <div className="text-gray-200">Support Available</div>
              </motion.div>
              <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                <div className="text-4xl font-bold text-green-400 mb-2">2</div>
                <div className="text-gray-200">Training Centers</div>
              </motion.div>
              <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                <div className="text-4xl font-bold text-purple-400 mb-2">15K+</div>
                <div className="text-gray-200">Students Trained</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="container-max py-20 space-y-32">
        {/* Contact Form & Info Section */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-20 rounded-3xl relative overflow-hidden">
          {/* Background decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-400/10 to-pink-400/10 rounded-full blur-2xl"></div>
          
          <motion.div 
            className="text-center mb-16 relative z-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Ready to start your programming journey? Contact us today and take the first step towards your dream career!
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border border-gray-100 dark:border-gray-700"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold mb-6 text-blue-700 dark:text-blue-300 flex items-center">
                <Send className="w-6 h-6 mr-2 text-blue-500" /> Send a Message
              </h3>
              
              {status === 'success' ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-green-600 dark:text-green-400 mb-2">Message Sent Successfully!</h4>
                  <p className="text-gray-600 dark:text-gray-400">Thank you for reaching out. We'll get back to you soon!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <FloatingLabelInput
                      id="name"
                      name="name"
                      label="Full Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                    <FloatingLabelInput
                      id="email"
                      name="email"
                      type="email"
                      label="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <FloatingLabelInput
                      id="phone"
                      name="phone"
                      type="tel"
                      label="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                    <FloatingLabelInput
                      id="subject"
                      name="subject"
                      label="Subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <FloatingLabelInput
                    as="textarea"
                    id="message"
                    name="message"
                    label="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                  />
                  
                  {status === 'error' && (
                    <div className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
                      <span className="text-red-700 dark:text-red-300">{errorMessage}</span>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    {status === 'loading' ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {/* Corporate Head Office */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border border-gray-100 dark:border-gray-700">
                <h3 className="text-xl font-bold mb-6 text-blue-700 dark:text-blue-300 flex items-center">
                  <Building className="w-6 h-6 mr-2 text-blue-500" /> Corporate Head Office
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">Address</div>
                      <div className="text-gray-600 dark:text-gray-400 text-sm">
                        1st Floor, Kohinoor Tower, 218, Jawahar Marg, Opposite HDFC Bank, North Rajmohalla, Raj Mohalla, Indore, Madhya Pradesh 452002, India
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-blue-500 mr-3" />
                    <a href="tel:+917649098000" className="text-blue-700 dark:text-blue-300 font-medium hover:underline">
                      +91 76490 98000
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-blue-500 mr-3" />
                    <a href="mailto:info@osopcoding.com" className="text-blue-700 dark:text-blue-300 font-medium hover:underline">
info@osopcoding.com
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-blue-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Mon - Sat: 9:00 AM - 8:00 PM</span>
                  </div>
                </div>
              </div>

              {/* Training Center */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border border-gray-100 dark:border-gray-700">
                <h3 className="text-xl font-bold mb-6 text-green-700 dark:text-green-300 flex items-center">
                  <GraduationCap className="w-6 h-6 mr-2 text-green-500" /> Training Center
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">Address</div>
                      <div className="text-gray-600 dark:text-gray-400 text-sm">
                        2nd Floor, Sundaram Complex, Bhawarkua Main Rd, Transport Nagar, Indore, Madhya Pradesh 452010
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-green-500 mr-3" />
                    <a href="tel:+917649099000" className="text-green-700 dark:text-green-300 font-medium hover:underline">
                      +91 76490 99000
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-green-500 mr-3" />
                    <a href="mailto:training@osopcoding.com" className="text-green-700 dark:text-green-300 font-medium hover:underline">
training@osopcoding.com
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-600 dark:text-gray-400">Mon - Sat: 10:00 AM - 7:00 PM</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Maps Section */}
        <section className="bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-20 rounded-3xl relative overflow-hidden">
          {/* Background decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/10 to-emerald-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-teal-400/10 to-cyan-400/10 rounded-full blur-2xl"></div>
          
          <motion.div 
            className="text-center mb-16 relative z-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl mb-6 shadow-lg">
              <Globe className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
              Find Our Locations
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Visit us at either of our training centers for personalized guidance and support
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Corporate Head Office Map */}
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300 flex items-center">
                  <Building className="w-6 h-6 mr-2 text-blue-500" /> Corporate Head Office
                </h3>
              </div>
              <div className="p-6">
                <MapPointComponent 
                  location="corporate"
                  title="Corporate Head Office"
                  address="1st Floor, Kohinoor Tower, 218, Jawahar Marg, Opposite HDFC Bank, North Rajmohalla, Raj Mohalla, Indore, Madhya Pradesh 452002"
                  coordinates={[22.7155, 75.8391]}
                  color="bg-blue-500"
                  phone="+91 76490 98000"
                  email="info@osopcoding.com"
                  timing="Mon - Sat: 9:00 AM - 8:00 PM"
                />
              </div>
            </motion.div>

            {/* Training Center Map */}
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="p-6 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-xl font-bold text-green-700 dark:text-green-300 flex items-center">
                  <GraduationCap className="w-6 h-6 mr-2 text-green-500" /> Training Center
                </h3>
              </div>
              <div className="p-6">
                <MapPointComponent 
                  location="training"
                  title="Training Center"
                  address="2nd Floor, Sundaram Complex, Bhawarkua Main Rd, Transport Nagar, Indore, Madhya Pradesh 452010"
                  coordinates={[22.6957, 75.8630]}
                  color="bg-green-500"
                  phone="+91 76490 99000"
                  email="training@osopcoding.com"
                  timing="Mon - Sat: 10:00 AM - 7:00 PM"
                />
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;